/* eslint-disable @next/next/no-img-element */
import React from 'react';
import { SearchPredictionSource } from 'utils/google-maps/types';
import { testIds } from 'constants/test-constants';
import styles from './style.module.scss';
import { sanitizeTestid } from 'utils/stringSanitization';
import { buildClassName } from 'utils/build-class-name';
import { isProvinceLabel } from 'utils/search-predictions';
import { standardizedCityName } from 'utils/standardized-city-name';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import { translateLabel } from 'utils/search-predictions-localizer';

import SearchPrediction, { convertSearchSuggestionToSearchPrediction } from 'data/search-predictions';
import type { SearchAgentPrediction, SearchSuggestions } from 'components/suggested-location-dropdown';

interface Props {
  title: string;
  Icon: any;
  selectedResult: number;
  searchPredictions: SearchPrediction[];
  searchSuggestions?: SearchSuggestions[];
  searchAgentPredictions?: SearchAgentPrediction[];
  onClick: (arg: SearchPrediction) => void;
  pushToRoute?: (searchSuggestion: SearchSuggestions) => void;
}

export default function LocationDropdownSection({ searchPredictions, title, Icon, onClick, selectedResult, searchSuggestions, searchAgentPredictions }: Props) {
  const { t } = useTranslation(Namespaces.SEARCH);
  const getMatchingTextEmphasizedAsHtml = ({ description, matchedSubstrings }: Record<string, any>) => {
    let html = description;
    if (matchedSubstrings?.length) {
      html = description.slice(0, matchedSubstrings[0].offset);
      matchedSubstrings.forEach(function(match: Record<string, any>, index: number) {
        html += '<strong>' + description.slice(match.offset, match.offset + match.length) + '</strong>';
        const start = match.offset + match.length;
        const nextMatch = matchedSubstrings[index + 1];
        const end = nextMatch ? nextMatch.offset : description.length;
        html += description.slice(start, end);
      });
    }
    return html;
  };

  const formatLocation = (location: string, searchPrediction: SearchPrediction) => {
    // Handle null, undefined, or empty location strings
    // This should never happen, but if malformed searchPrediction was saved with no required fields it will crash
    if (!location) {
      return searchPrediction.description || '';
    }
    
    const splitLocation = location.split(',');
    let firstArea = splitLocation[0];
    if (searchPrediction.group === 'listings') {
      firstArea = [firstArea, splitLocation[1]].join();
    }
    const restOfDescription = location.replace(firstArea, '');
    if (isProvinceLabel(searchPrediction.label)) {
      firstArea = firstArea.replace('-', ' ');
    }
    firstArea = standardizedCityName(firstArea.toLowerCase());
    
    return [firstArea, restOfDescription].join('');
  };

  const areResultsFromGoogle = () => searchPredictions.some(result => result.source === SearchPredictionSource.GOOGLE);

  return (
    <div className={styles.component} data-testid={sanitizeTestid(title) + testIds.dropdownSection}>
      {
        (searchPredictions && !!searchPredictions.length || !!searchSuggestions?.length) &&
        <div className={styles['section-title']}>
          <span><Icon /> {title}</span>
          {areResultsFromGoogle() &&
            <span className={styles['powered-by-google']}>
              <img src='/next/assets/images/powered_by_google.png' alt={t(LOCALE_SEARCH_PATHS.GOOGLE_ALT_TEXT, { defaultValue: 'Google' })} />
            </span>
          }
        </div>
      }
      {
        searchPredictions?.map((searchPrediction, index) => {
          if (!searchPrediction.description) return null;

          const matchedLocation = getMatchingTextEmphasizedAsHtml(searchPrediction);
          const locationText = formatLocation(matchedLocation, searchPrediction);
          
          const renderLabel = () => {
            // No label to render (e.g., some location predictions may not have labels)
            if (!searchPrediction.label) return null;
            
            // Handle compound labels (e.g., "active - for sale", "sold - sold")
            // These typically come from listing predictions with status information
            if (searchPrediction.label.includes('- ')) {
              const [statusLabel, salesLabel] = searchPrediction.label.split('- ');
              
              // Determine styling based on listing status (inactive listings get "sold" styling)
              const isInactive = statusLabel?.includes('inactive');
              
              return (
                <div className={styles.label}>
                  {/* Translate the main status (e.g., "active" → "Actif") */}
                  {translateLabel(t, statusLabel)} {salesLabel && 
                    /* Translate and style the sales status in brackets */
                    <span className={isInactive ? styles.sold : styles.sale}>
                      [{translateLabel(t, salesLabel)}]
                    </span>
                  }
                </div>
              );
            }
            
            // Handle simple labels (e.g., "city", "school", "neighbourhood")
            // These come from location predictions indicating the type of location
            return <div className={styles.label}>{translateLabel(t, searchPrediction.label)}</div>;
          };

          return (
            <div
              className={`${styles['location-option']} ${selectedResult === searchPrediction.identifier ? styles['active'] : ''}`}
              key={index}
              onClick={() => onClick(searchPrediction)}
            >
              <div dangerouslySetInnerHTML={{ __html: locationText }} />
              {renderLabel()}
            </div>
          );
        })
      }
      {
        searchSuggestions?.map((searchSuggestion, index) => {
          const { label } = searchSuggestion;
          const [suggestionLabel, suggestionTypeLabel] = label?.split('- ') || [];
          return (
            <div key={index} className={styles['suggested-link']}>
              <a className={`${styles['location-option']} ${selectedResult === searchSuggestion.identifier ? styles['active'] : ''}`}
                onClick={async () => {
                  // In order to save our search suggestions as a "recent search" when clicked, we must treat it as a search prediction
                  const searchPrediction = await convertSearchSuggestionToSearchPrediction(searchSuggestion) as SearchPrediction;
                  onClick(searchPrediction);
                }}>
                {suggestionLabel}
                <div className={styles.label}>{translateLabel(t, suggestionTypeLabel)}</div>
              </a>
            </div>
          );
        })
      }
      {
        searchAgentPredictions?.map((searchAgentPrediction, index) => {
          const { label } = searchAgentPrediction;
          const [suggestionLabel, suggestionTypeLabel] = label?.split('- ') || [];
          return (
            <div key={index} className={styles['suggested-link']} onClick={() => onClick(searchAgentPrediction as SearchPrediction)}>
              <a className={buildClassName(styles['location-option'], selectedResult === searchAgentPrediction.identifier ? styles['active'] : '')}>
                {suggestionLabel}
                <div className={styles.label}>{translateLabel(t, suggestionTypeLabel)}</div>
              </a>
            </div>
          );
        })
      }
    </div>
  );
}
